import { FormHiddenInputField, MainButton } from '@eventr/common-storybook'
import { zodResolver } from '@hookform/resolvers/zod'
import { Stack } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import i18n from '../../../i18n/i18next'
import { logEvent } from '../../firebase/firebase'
import { useGuestStore } from '../../guest/guest.store'
import { NavigationView } from '../../layout/NavigationView'
import { useRegStore } from '../registrationProvider'

const validationSchema = z
    .object({
        password: z
            .string()
            .min(5, {
                message: i18n.t('authentication.add-details.at-least-five'),
            })
            .regex(/\d/, {
                message: i18n.t('authentication.add-details.one-number'),
            }),
        confirmPassword: z
            .string()
            .min(5, {
                message: i18n.t('authentication.add-details.at-least-five'),
            })
            .regex(/\d/, {
                message: i18n.t('authentication.add-details.one-number'),
            }),
    })
    .superRefine(({ confirmPassword, password }, ctx) => {
        if (confirmPassword !== password) {
            ctx.addIssue({
                path: ['confirmPassword'],
                code: 'custom',
                message: i18n.t('authentication.add-details.no-match'),
            })
        }
    })

type ValidationSchema = z.infer<typeof validationSchema>

export const SetPasswordPage = () => {
    const { inviteUserData, setInviteData } = useRegStore((s) => {
        return {
            inviteUserData: s.inviteUserData,
            setInviteData: s.setInviteData,
        }
    })
    const methods = useForm<ValidationSchema>({
        resolver: zodResolver(validationSchema),
    })
    const { handleSubmit, control } = methods

    const onSubmit: SubmitHandler<ValidationSchema> = (data) => {
        setInviteData({
            password: data.password,
        })
        if (inviteUserData?.flow_type === 'invite') {
            logEvent('sign_up_set_profile_pic', {
                guestCopy: useGuestStore.getState().cloneOnSignUp,
            })
            window.pushPath('/authentication/addProfilePic')
        } else {
            logEvent('sign_up_terms', {
                guestCopy: useGuestStore.getState().cloneOnSignUp,
            })
            window.pushPath('/authentication/termsAndPolicies')
        }
    }

    function backButtonAction() {
        window.goBack()
    }

    const firstPasswordField = (
        <FormHiddenInputField
            id="passwordField"
            control={control}
            name="password"
            fieldLabel={i18n.t('authentication.sign-in.password')}
            required
        />
    )

    const secondPasswordField = (
        <FormHiddenInputField
            id="confirmPasswordField"
            control={control}
            name="confirmPassword"
            fieldLabel={i18n.t('authentication.add-details.confirm-password')}
            required
        />
    )

    const body = (
        <Stack padding="16px" gap="8px">
            <FormProvider {...methods}>
                {firstPasswordField}

                {secondPasswordField}
            </FormProvider>
        </Stack>
    )

    const footer = (
        <Stack padding="16px">
            <MainButton
                id="continueButton"
                text={i18n.t('general.continue')}
                onClick={() => handleSubmit(onSubmit)()}
            />
        </Stack>
    )

    return (
        <NavigationView
            navigationBarTitle={i18n.t(
                'authentication.add-details.create-password'
            )}
            navigationBarSubtitle={i18n.t(
                'authentication.add-details.password-instructions'
            )}
            navigationBarDisplayMode="large"
            usePageTransition
            backButtonAction={backButtonAction}
            isRoot
            footer={footer}
        >
            {body}
        </NavigationView>
    )
}
